import React from 'react';
import type { LoyaltyBoost, LoyaltyBoostMode, LoyaltyProgramType } from '../../../shared/types';
import { LightbulbIcon } from '../icons/UiIcons';

interface BoostConfigProps {
    value: LoyaltyBoost;
    onChange: (boost: LoyaltyBoost) => void;
    /** Type du programme — détermine la disponibilité de 'initial_stamps'. */
    programType: LoyaltyProgramType;
    disabled?: boolean;
}

interface ModeDef {
    id: LoyaltyBoostMode;
    label: string;
    short: string;
    psychology: string;
    /** Si défini, le mode est uniquement disponible pour ces types. */
    onlyForTypes?: LoyaltyProgramType[];
}

const MODES: ModeDef[] = [
    {
        id: 'first_visit',
        label: 'Premier passage boosté',
        short: 'Points/tampons doublés à la première visite',
        psychology: 'Effet Wow immédiat. Le client voit que sa fidélité est récompensée dès le départ — il a déjà investi mentalement et reviendra plus facilement.',
    },
    {
        id: 'first_week',
        label: 'Semaine boost',
        short: 'Pendant 7 jours après l\'inscription, tout est ×2',
        psychology: 'Crée un sentiment d\'urgence sain. Le client est incité à revenir plusieurs fois rapidement, ce qui ancre l\'habitude (3 visites = 60% de chance de devenir régulier).',
    },
    {
        id: 'initial_stamps',
        label: 'Tampons offerts à l\'inscription',
        short: '1 à 3 tampons crédités dès l\'adhésion',
        psychology: 'Illusion de progrès (Endowed Progress Effect). Un client qui démarre à 2/10 termine sa carte 2× plus souvent qu\'un client qui démarre à 0/10.',
        onlyForTypes: ['stamps'],
    },
];

const BoostConfig: React.FC<BoostConfigProps> = ({ value, onChange, programType, disabled }) => {
    // Pour éviter de propager des `undefined` jusqu'à Firestore, on construit
    // l'objet boost en n'incluant que les clés effectivement définies.
    const setEnabled = (enabled: boolean) => {
        if (!enabled) {
            onChange({ enabled: false });
            return;
        }
        const mode = value.mode ?? 'first_visit';
        const next: LoyaltyBoost = { enabled: true, mode };
        if (mode === 'initial_stamps') next.initialStamps = value.initialStamps ?? 2;
        onChange(next);
    };

    const setMode = (mode: LoyaltyBoostMode) => {
        const next: LoyaltyBoost = { enabled: true, mode };
        if (mode === 'initial_stamps') next.initialStamps = value.initialStamps ?? 2;
        onChange(next);
    };

    const setInitialStamps = (n: number) => {
        onChange({ ...value, initialStamps: Math.max(1, Math.min(3, n)) });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-bold text-gray-900">Boost de démarrage</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Une accélération initiale pour ancrer l'habitude de fidélité chez les nouveaux clients.
                    </p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={value.enabled}
                    onClick={() => !disabled && setEnabled(!value.enabled)}
                    disabled={disabled}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        value.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                    } disabled:opacity-50`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        value.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                </button>
            </div>

            {value.enabled && (
                <fieldset className="space-y-2" disabled={disabled}>
                    <legend className="sr-only">Mode de boost</legend>
                    {MODES.map(mode => {
                        const available = !mode.onlyForTypes || mode.onlyForTypes.includes(programType);
                        const isSelected = value.mode === mode.id;
                        return (
                            <label
                                key={mode.id}
                                className={`block p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                                    !available
                                        ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-50'
                                        : isSelected
                                            ? 'border-indigo-500 bg-indigo-50'
                                            : 'border-gray-200 bg-white hover:border-indigo-300'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    <input
                                        type="radio"
                                        name="boost-mode"
                                        value={mode.id}
                                        checked={isSelected}
                                        onChange={() => available && setMode(mode.id)}
                                        disabled={!available}
                                        className="mt-1"
                                    />
                                    <div className="flex-1">
                                        <div className="font-bold text-sm text-gray-900">{mode.label}</div>
                                        <div className="text-xs text-gray-600 mt-0.5">{mode.short}</div>
                                        {!available && (
                                            <div className="text-xs text-amber-600 italic mt-1">
                                                Disponible uniquement en mode Tampons
                                            </div>
                                        )}
                                        <div className="text-xs text-brand-primary dark:text-brand-primary/90 mt-1.5 italic inline-flex items-start gap-1.5">
                                            <LightbulbIcon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                            <span>{mode.psychology}</span>
                                        </div>
                                        {mode.id === 'initial_stamps' && isSelected && available && (
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-xs text-gray-700">Tampons offerts :</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={3}
                                                    value={value.initialStamps ?? 2}
                                                    onChange={e => setInitialStamps(parseInt(e.target.value) || 1)}
                                                    className="w-16 p-1 text-sm bg-white border border-gray-400 rounded outline-none focus:ring-1 focus:ring-brand-500 focus:border-indigo-400"
                                                />
                                                <span className="text-xs text-gray-500">(1 à 3)</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </fieldset>
            )}
        </div>
    );
};

export default BoostConfig;
